import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [charCount, setCharCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === 'message') {
      if (value.length <= 500) {
        setFormData({ ...formData, [name]: value });
        setCharCount(value.length);
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.subject) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.message.length > 500) {
      alert('Message cannot exceed 500 characters');
      return;
    }

    setIsSubmitting(true);

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d70inq6th28qd804crog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setShowSuccess(true);
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          subject: '',
          message: '',
        });
        setCharCount(0);
        setTimeout(() => setShowSuccess(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="pt-14 pb-8 lg:pt-20 lg:pb-10 bg-gradient-to-b from-white to-gray-900/5">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Left Column - Contact Info */}
          <div className="lg:col-span-2">
            <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              Let's Start Exporting Together
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed mb-10">
              Get in touch with us to discuss your export requirements. We're here to provide you with premium quality products and reliable service.
            </p>

            {/* Contact Details */}
            <div className="space-y-6">
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">COMPANY</p>
                <p className="text-lg font-bold text-gray-900">JB GlobeX</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">LOCATION</p>
                <p className="text-base font-bold text-gray-900">Kolar Gold Fields, Karnataka, India</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">PHONE</p>
                <a href="tel:+919743475615" className="block text-lg font-bold text-teal-600 hover:text-teal-700 cursor-pointer">
                  +91 9743475615
                </a>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">EMAIL</p>
                <a href="mailto:info@jbglobex.com" className="text-lg font-bold text-teal-600 hover:text-teal-700 cursor-pointer">
                  info@jbglobex.com
                </a>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">WHATSAPP</p>
                <a
                  href="https://wa.me/919743475615"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-bold text-teal-600 hover:text-teal-700 cursor-pointer"
                >
                  +91 9743475615
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl p-8 lg:p-10 shadow-lg">
              {/* Success Message */}
              {showSuccess && (
                <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-center space-x-3">
                  <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                  <p className="text-green-800 font-medium">
                    Thank you! Your message has been sent successfully.
                  </p>
                </div>
              )}

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit}>
                {/* Two Column Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-semibold text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 transition-colors"
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                {/* Two Column Row */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 transition-colors"
                      placeholder="+91 1234567890"
                    />
                  </div>
                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-700 mb-2">
                      Subject *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 transition-colors"
                      placeholder="How can we help?"
                    />
                  </div>
                </div>

                {/* Message Field */}
                <div className="mb-6">
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 transition-colors resize-vertical"
                    placeholder="Tell us about your requirements..."
                  ></textarea>
                  <div className="text-right mt-2">
                    <span className="text-xs text-gray-500">{charCount}/500</span>
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-teal-600 text-white rounded-xl font-bold text-lg hover:bg-teal-700 transition-all hover:shadow-lg flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  <span>{isSubmitting ? 'Sending...' : 'Send Message'}</span>
                  {!isSubmitting && <i className="ri-send-plane-fill"></i>}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}